/**
 * Data Import/Export Utilities
 * Functions for CSV and Excel file processing
 */

import Papa from "papaparse";
import * as XLSX from "xlsx";
import type { ImportResult, ExportOptions } from "../types/api.types";

/**
 * Parse CSV file to array of objects
 */
export async function parseCSV<T extends Record<string, unknown>>(
  file: File,
  options?: {
    header?: boolean;
    skipEmptyLines?: boolean;
    transformHeader?: (header: string) => string;
  },
): Promise<ImportResult<T>> {
  return new Promise((resolve) => {
    const errors: ImportResult<T>["errors"] = [];
    const data: T[] = [];

    Papa.parse<T>(file, {
      header: options?.header ?? true,
      skipEmptyLines: options?.skipEmptyLines ?? true,
      transformHeader:
        options?.transformHeader ??
        ((h) => h.trim().toLowerCase().replace(/\s+/g, "_")),
      complete: (results) => {
        results.data.forEach((row, index) => {
          if (results.errors[index]) {
            errors.push({
              row: index + 1,
              error: results.errors[index].message,
              data: row as Record<string, unknown>,
            });
          } else {
            data.push(row);
          }
        });

        resolve({
          imported: data.length,
          skipped: 0,
          failed: errors.length,
          data,
          errors,
        });
      },
      error: (error) => {
        resolve({
          imported: 0,
          skipped: 0,
          failed: 1,
          data: [],
          errors: [{ row: 0, error: error.message }],
        });
      },
    });
  });
}

/**
 * Parse Excel file to array of objects
 */
export async function parseExcel<T extends Record<string, unknown>>(
  file: File,
  options?: {
    sheetIndex?: number;
    sheetName?: string;
    transformHeader?: (header: string) => string;
  },
): Promise<ImportResult<T>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });

        // Get the sheet to parse
        const sheetName =
          options?.sheetName ?? workbook.SheetNames[options?.sheetIndex ?? 0];
        const worksheet = workbook.Sheets[sheetName];

        if (!worksheet) {
          resolve({
            imported: 0,
            skipped: 0,
            failed: 1,
            data: [],
            errors: [{ row: 0, error: `Sheet "${sheetName}" not found` }],
          });
          return;
        }

        // Convert to JSON
        const rawData =
          XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet);

        // Transform headers if function provided
        const transformHeader =
          options?.transformHeader ??
          ((h: string) => h.trim().toLowerCase().replace(/\s+/g, "_"));

        const transformedData = rawData.map((row) => {
          const newRow: Record<string, unknown> = {};
          Object.entries(row).forEach(([key, value]) => {
            newRow[transformHeader(key)] = value;
          });
          return newRow as T;
        });

        resolve({
          imported: transformedData.length,
          skipped: 0,
          failed: 0,
          data: transformedData,
          errors: [],
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsBinaryString(file);
  });
}

/**
 * Export data to CSV and trigger download
 */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  options: ExportOptions,
): void {
  const { filename = "export", columns, includeHeaders = true } = options;

  // Filter columns if specified
  const filteredData = columns
    ? data.map((row) => {
        const filtered: Record<string, unknown> = {};
        columns.forEach((col) => {
          filtered[col] = row[col];
        });
        return filtered;
      })
    : data;

  const csv = Papa.unparse(filteredData, {
    header: includeHeaders,
  });

  downloadFile(csv, `${filename}.csv`, "text/csv;charset=utf-8;");
}

/**
 * Export data to Excel and trigger download
 */
export function exportToExcel<T extends Record<string, unknown>>(
  data: T[],
  options: ExportOptions & { sheetName?: string },
): void {
  const { filename = "export", columns, sheetName = "Sheet1" } = options;

  // Filter columns if specified
  const filteredData = columns
    ? data.map((row) => {
        const filtered: Record<string, unknown> = {};
        columns.forEach((col) => {
          filtered[col] = row[col];
        });
        return filtered;
      })
    : data;

  const worksheet = XLSX.utils.json_to_sheet(filteredData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  downloadFile(blob, `${filename}.xlsx`);
}

/**
 * Export data to JSON and trigger download
 */
export function exportToJSON<T extends Record<string, unknown>>(
  data: T[],
  options: ExportOptions,
): void {
  const { filename = "export", columns } = options;

  // Filter columns if specified
  const filteredData = columns
    ? data.map((row) => {
        const filtered: Record<string, unknown> = {};
        columns.forEach((col) => {
          filtered[col] = row[col];
        });
        return filtered;
      })
    : data;

  const json = JSON.stringify(filteredData, null, 2);
  downloadFile(json, `${filename}.json`, "application/json");
}

/**
 * Generic export function
 */
export function exportData<T extends Record<string, unknown>>(
  data: T[],
  options: ExportOptions,
): void {
  switch (options.format) {
    case "csv":
      exportToCSV(data, options);
      break;
    case "xlsx":
      exportToExcel(data, options);
      break;
    case "json":
      exportToJSON(data, options);
      break;
    default:
      throw new Error(`Unsupported export format: ${options.format}`);
  }
}

/**
 * Download a file
 */
function downloadFile(
  content: string | Blob,
  filename: string,
  mimeType?: string,
): void {
  const blob =
    content instanceof Blob
      ? content
      : new Blob([content], { type: mimeType ?? "text/plain" });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Validate file type
 */
export function isValidFileType(file: File, allowedTypes: string[]): boolean {
  return allowedTypes.includes(file.type);
}

/**
 * Get file extension
 */
export function getFileExtension(filename: string): string {
  return filename
    .slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2)
    .toLowerCase();
}

/**
 * Determine file type from extension
 */
export function getFileTypeFromExtension(
  filename: string,
): "csv" | "xlsx" | "xls" | "json" | "unknown" {
  const ext = getFileExtension(filename);
  switch (ext) {
    case "csv":
      return "csv";
    case "xlsx":
      return "xlsx";
    case "xls":
      return "xls";
    case "json":
      return "json";
    default:
      return "unknown";
  }
}
